import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import { IAPIFetchRequestOptions, IAPIListResponse, IAPIFetchResponse } from '@sondermind/utilities/models-http';
import { IProviderNetworkCompany } from '@sondermind/utilities/models-insurance/client';
import {
  IClientMatchingProfile,
  IClientMatchingProfileApiResponse,
  clientMatchingResponseTransformer
} from '@sondermind/utilities/models-matching';
import { snakeCaseTransformer } from '@sondermind/utilities/tools';
import { IMatchingProfileUpdatePayload } from '../models/matching-profile.interface';

@Injectable({
  providedIn: 'root'
})
export class MatchingProfileHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) { }

  get urlBase(): string {
    return `${this.configurationService.env.svcBase}/referrals/matching_profiles`;
  }

  /**
   * Retrieve all matching profile data associated with a client user.
   * If a client does not have a matching profile (such as for provider-invited),
   * return an empty profile.
   */
  fetchMostRecent(opts: Partial<IAPIFetchRequestOptions> = {}): Observable<IClientMatchingProfile> {
    const params = this.http.generateListParams(opts);

    return this.http
      .get<IAPIListResponse<IClientMatchingProfileApiResponse>>(this.urlBase, params)
      .pipe(
        // Only use the most recent NCR for the matching profile
        map((resp) => (resp?.data?.length > 0
          ? clientMatchingResponseTransformer(resp.data[resp.data.length - 1])
          : {
            providerNetworkCompanies: [] as IProviderNetworkCompany[],
            reasons: [] as Array<{ name: string; desc: string; }>
          } as IClientMatchingProfile
        )),
        this.http.handleHttpError(opts.error)
      );
  }

  /**
   * Update a matching profile
   * @param matchingProfileId - The ID of the matching profile to update
   * @param payload - The partial update payload
   */
  update$(
    matchingProfileId: number,
    payload: Partial<IMatchingProfileUpdatePayload>
  ): Observable<IAPIFetchResponse<IClientMatchingProfile> | null> {
    const url = `${this.urlBase}/${matchingProfileId}`;
    const transformedPayload = snakeCaseTransformer(payload);

    return this.http
      .patch<IAPIFetchResponse<IClientMatchingProfile>>(
      url,
      transformedPayload
    ).pipe(
      this.http.handleHttpError()
    );
  }
}
